<template>
  <div class="dashboard-shell">
    <AppTopbar />
    <main class="page-shell">
      <div class="container-readable stack-lg">
        
        <!-- Hero Banner עליון בעיצוב המותג -->
        <section class="hero-banner">
          <div class="hero-container">
            <div class="hero-content">
              <router-link to="/" class="back-link">
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5">
                  <path d="M19 12H5M12 19l-7-7 7-7"/>
                </svg>
                חזרה לאזור האישי
              </router-link>

              <h1 class="hero-title">הוספת חבילה</h1>
              <p class="hero-subtitle">הוסיפו חבילת דקות חד פעמית למספר הפעיל שלכם.</p>
            </div>

            <div v-if="activePhoneLabel" class="current-chip">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"/>
              </svg>
              <span>מספר פעיל: {{ activePhoneLabel }}</span>
            </div>
          </div>
        </section>

        <!-- פריסת הדף: גריד קוביות מימין, סיכום משמאל -->
        <section class="plan-layout">
          
          <!-- פאנל החיפוש וגריד הקוביות -->
          <div class="surface card-block stack-md plan-list-panel">
            <div class="search-row">
              <div class="search-input-wrapper">
                <svg class="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                  <circle cx="11" cy="11" r="8"/>
                  <line x1="21" y1="21" x2="16.65" y2="16.65"/>
                </svg>
                <input v-model="searchTerm" type="text" class="input-control" placeholder="חיפוש לפי שם חבילה או מחיר..." />
              </div>
              <span class="count-pill">{{ filteredPackages.length }} תוצאות</span>
            </div>

            <p v-if="loading" class="muted loading-state">טוען חבילות זמינות...</p>

            <div v-else-if="filteredPackages.length > 0" class="packages-grid-container">
              <!-- גריד קוביות החבילות -->
              <div class="packages-grid">
                <button
                  v-for="pkg in visibleResults" :key="pkg.id" type="button"
                  class="package-card" :class="{ selected: String(selectedId) === String(pkg.id) }"
                  @click="selectPackage(pkg.id)"
                >
                  <div class="card-header">
                    <div class="pkg-icon-wrap">
                      <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                        <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z"/>
                        <polyline points="3.27 6.96 12 12.01 20.73 6.96"/>
                        <line x1="12" y1="22.08" x2="12" y2="12"/>
                      </svg>
                    </div>
                    <div class="selection-badge" v-if="String(selectedId) === String(pkg.id)">
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="3">
                        <polyline points="20 6 9 17 4 12"/>
                      </svg>
                      נבחר
                    </div>
                  </div>

                  <div class="card-body">
                    <span class="package-title">{{ pkg.name }}</span>
                  </div>

                  <div class="card-footer">
                    <span v-if="pkg.price" class="package-price">₪{{ pkg.price }}</span>
                    <span class="price-type">חד פעמי</span>
                  </div>
                </button>
              </div>

              <button v-if="hasMoreResults" type="button" class="btn btn-ghost show-more-btn" @click="showAllResults = !showAllResults">
                {{ showAllResults ? 'להציג פחות' : 'להציג את כל התוצאות' }}
              </button>
            </div>

            <p v-else class="muted empty-state">לא נמצאו חבילות התואמות לחיפוש.</p>
          </div>

          <!-- פאנל סיכום והוספה (Sticky) -->
          <aside class="surface card-block stack-md plan-summary-panel">
            <h2 class="section-title">סיכום בחירה</h2>
            
            <div class="summary-box stack-sm">
              <span class="summary-key">חבילה נבחרת</span>
              <p v-if="selectedPackage" class="summary-value">{{ selectedPackage.name }}</p>
              <p v-else class="summary-value muted-placeholder">עדיין לא נבחרה חבילה</p>
              
              <div v-if="selectedPackage && selectedPackage.price" class="price-summary-row">
                <span>עלות חד-פעמית:</span>
                <span class="price-amount">₪{{ selectedPackage.price }}</span>
              </div>
            </div>

            <div class="actions-row">
              <button class="btn btn-primary" :disabled="!selectedId || saving" @click="confirm">
                <span v-if="!saving">הוסף חבילה</span>
                <span v-else>מוסיף...</span>
              </button>
              <router-link to="/" class="btn btn-ghost">חזרה ללא שינוי</router-link>
            </div>

            <div v-if="error" class="error-inline">{{ error }}</div>
            <div v-if="successMsg" class="success-inline">{{ successMsg }}</div>
          </aside>
        </section>

        <!-- היסטוריית חבילות שנוספו -->
        <section v-if="addedPackages.length" id="added-packages" class="surface card-block stack-md history-section">
          <div class="inline-row space-between">
            <div class="section-header-wrap">
              <h2 class="section-title">חבילות שנוספו לאחרונה</h2>
              <p class="muted">רשימת השירותים שהופעלו בחשבון זה</p>
            </div>
            <button class="btn btn-ghost-sml refresh-btn" :disabled="loadingAdded" @click="fetchAddedPackages" title="רענון">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                <path d="M23 4v6h-6M1 20v-6h6"/>
                <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
              </svg>
              רענון
            </button>
          </div>

          <div class="added-list">
            <div v-for="(p, i) in addedPackages" :key="i" class="added-row">
              <div class="added-main">
                <span class="added-name">{{ p.name }}</span>
                <span class="added-date">{{ formatDate(p.date) }}</span>
              </div>
              <span class="added-badge" :class="{ ok: p.paid }">
                {{ p.paid ? '✓ עודכן' : '✕ נכשל' }}
              </span>
            </div>
          </div>
        </section>

      </div>
    </main>
    <AppFooter />
  </div>
</template>

<script>
import AppFooter from '@/components/AppFooter.vue'
import AppTopbar from '@/components/AppTopbar.vue'

export default {
  name: 'ChangePackages',
  components: { AppTopbar, AppFooter },
  data() {
    return {
      loading: false,
      packages: [],
      selectedId: null,
      searchTerm: '',
      showAllResults: false,
      saving: false,
      error: '',
      successMsg: '',
      activePhone: '',
      addedPackages: [],
      loadingAdded: false,
      numberIndex: parseInt(localStorage.getItem('selected_number_index') || '0', 10),
    }
  },
  computed: {
    filteredPackages() {
      const term = this.searchTerm.trim().toLowerCase()
      const sorted = [...this.packages].sort((a, b) => Number(a.price || 0) - Number(b.price || 0))
      if (!term) return sorted
      return sorted.filter(p => String(p.name || '').toLowerCase().includes(term) || String(p.price || '').includes(term))
    },
    visibleResults() { return this.showAllResults ? this.filteredPackages : this.filteredPackages.slice(0, 6) },
    hasMoreResults() { return this.filteredPackages.length > 6 },
    selectedPackage() { return this.packages.find(p => String(p.id) === String(this.selectedId)) || null },
    activePhoneLabel() { return this.formatPhone(this.activePhone) },
  },
  mounted() {
    if (this.isNull(localStorage.getItem('user_token'))) { this.$router.push('/login'); return }
    this.fetchDashboard()
    this.fetchPackages()
    this.fetchAddedPackages()
  },
  methods: {
    fetchDashboard() {
      this.api({ action: 'get_my_dashboard_data', method: 'get' }, (response) => {
        if (response?.data?.success) {
          const numbers = response.data.numbers || []
          const idx = Math.min(this.numberIndex, Math.max(0, numbers.length - 1))
          if (numbers.length) this.activePhone = numbers[idx]?.phone || ''
          else if (response.data.user) this.activePhone = response.data.user.phone || ''
        }
      })
    },
    fetchPackages() {
      this.loading = true
      this.api({ action: 'get_add_packages_list', method: 'get' }, (response) => {
        this.packages = response?.data?.packages || []
        this.loading = false
      }, () => { this.loading = false })
    },
    selectPackage(id) { this.selectedId = id },
    fetchAddedPackages() {
      this.loadingAdded = true
      this.api({ action: 'get_my_added_packages', method: 'get' }, (response) => {
        this.addedPackages = response?.data?.packages || []
        this.loadingAdded = false
        if (this.$route.hash === '#added-packages') {
          this.$nextTick(() => this.scrollToAdded())
        }
      }, () => { this.loadingAdded = false })
    },
    scrollToAdded() {
      const el = document.getElementById('added-packages')
      if (el) el.scrollIntoView({ behavior: 'smooth', block: 'start' })
    },
    confirm() {
      if (!this.selectedId) return
      this.saving = true; this.error = ''; this.successMsg = ''
      this.api({
        action: 'add_my_package',
        data: { package_id: this.selectedId, amount: 1, number_index: this.numberIndex },
        method: 'post'
      }, (response) => {
        if (response?.data?.success) {
          const chosen = this.selectedPackage
          const name = response?.data?.package_name || chosen?.name || ''
          this.successMsg = 'החבילה נוספה בהצלחה — ' + name
          this.selectedId = null
          this.fetchAddedPackages()
        } else {
          const e = response?.data?.error
          this.error = e === 'no_payment_token' ? 'לא נמצא אמצעי תשלום שמור. עדכנו תשלום קודם.'
            : e === 'payment_failed' ? 'החיוב נכשל. בדקו את אמצעי התשלום.'
            : 'לא הצלחנו להוסיף את החבילה. נסו שוב.'
        }
        this.saving = false
      }, () => { this.error = 'שגיאה בהוספת החבילה.'; this.saving = false })
    },
    formatDate(d) {
      if (!d) return ''
      const dt = new Date(String(d).replace(' ', 'T'))
      if (isNaN(dt.getTime())) return d
      const pad = n => String(n).padStart(2, '0')
      return pad(dt.getDate()) + '/' + pad(dt.getMonth() + 1) + '/' + dt.getFullYear() + ' ' + pad(dt.getHours()) + ':' + pad(dt.getMinutes())
    },
    formatPhone(phone) {
      if (!phone) return ''
      const p = String(phone).replace(/\D/g, '')
      if (p.startsWith('972') && p.length === 12) return '0' + p.slice(3)
      return phone
    },
  }
}
</script>

<style lang="scss" scoped src="@/styles/pages/add-package.scss"></style>